import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { monetizationAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Zap, DollarSign, Clock, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

import { formatDateMonthDay } from '../../utils/timeFormat';
const InstantPayout = () => {
  const navigate = useNavigate();
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [requesting, setRequesting] = useState(false);
  // P0 (Mel directive 2026-08-19) — feature-flag driven from the backend
  // `/monetization/payout/info` response. When `enabled=false` the
  // request UI is hidden and a disabled-state banner replaces it.
  const featureEnabled = payoutInfo?.enabled !== false;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [infoRes, eligibleRes, historyRes, eligibilityRes] = await Promise.all([
        monetizationAPI.getPayoutInfo(),
        monetizationAPI.getEligiblePayouts(),
        monetizationAPI.getPayoutHistory(),
        api.get('/monetization/payout/eligibility')
      ]);
      setPayoutInfo(infoRes.data);
      setEligible(eligibleRes.data);
      setHistory(historyRes.data);
      setEligibility(eligibilityRes.data);
    } catch (error) {
      console.error('Error loading payout data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!selectedBooking) return;
    setRequesting(true);
    try {
      const res = await monetizationAPI.requestPayout(selectedBooking.booking_id);
      toast.success(res.data.message);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      // Feature-flag path: backend returns 503 { code: 'instant_pay_disabled', message, ... }
      const d = error.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'instant_pay_disabled') {
        toast.error(d.message || 'Instant Pay is temporarily disabled');
        // Reload info so the banner + disabled state reflect current server state.
        fetchData();
        setSelectedBooking(null);
        return;
      }
      const msg = typeof d === 'string' ? d : (d && (d.message || d.code)) || 'Failed to request payout';
      toast.error(msg);
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="instant-payout-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/settlements')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Settlements
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* P0 (Mel directive 2026-08-19) — Feature-flag banner. When
            Instant Pay is disabled at the backend, we show a clear
            explanation and hide the request UI. Standard payouts are
            unaffected (they run on the normal schedule via the admin
            path). */}
        {!featureEnabled && (
          <Alert
            className="mb-6 bg-amber-50 border-amber-300 text-amber-900"
            data-testid="instant-pay-disabled-banner"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-900">
              <span className="font-semibold block mb-1" data-testid="instant-pay-disabled-title">Instant Pay is temporarily disabled</span>
              <span className="text-sm">
                {payoutInfo?.disabled_reason
                 || "Instant Pay is temporarily unavailable systemwide. Your standard payouts are unaffected and will process on the normal schedule per each gig's payout terms. We'll notify you the moment Instant Pay is back on."}
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Hero */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Instant Payout</h1>
            <p className="text-gray-500">Get your earnings faster with a {payoutInfo?.fee_percent ?? 3}% processing fee</p>
          </div>
        </div>

        {/* 2026-06 — Instant Pay Availability (shown when NOT systemwide
            disabled). Payout terms are per-gig and disclosed on each gig;
            this page no longer implies a global 5-event/rating gate. */}
        {featureEnabled && (
          <Alert className="mb-6 bg-cyan-50 border-cyan-200" data-testid="instant-pay-availability-notice">
            <Info className="w-4 h-4 text-cyan-600" />
            <AlertDescription className="text-cyan-900">
              <span className="font-semibold block mb-1">Instant Pay Availability</span>
              <span className="text-sm">
                Instant Pay is available only for qualifying gigs that specifically show Instant Pay
                as the payout option. If a gig uses Standard Pay or another payment schedule, the
                payout terms shown on that gig will apply.
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* 2026-06 — Payout options explainer (matches the contractor
            agreement: terms are per-gig, disclosed before apply/accept). */}
        <Card className="mb-6 border-gray-200" data-testid="payout-options-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-cyan-500" />
              Instant Pay &amp; Payout Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-gray-700">
            <p>
              Payout terms are determined by the specific gig and are displayed to you before you
              apply or accept the assignment. Not every gig is eligible for Instant Pay.
            </p>
            <p>
              For eligible iEvntür Marketplace gigs, clients fund the booking through the platform
              and funds are held until the work requirements for the gig have been completed and
              approved. Eligible earnings may then become available under the payout option
              designated for that gig.
            </p>
            <p>
              Some clients operate under <span className="font-medium">Standard Pay</span> terms
              rather than Instant Pay. These gigs may have different payout schedules based on the
              client&apos;s payment terms. The applicable payout terms are clearly displayed on each
              gig before you apply.
            </p>
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
              <p className="font-semibold text-gray-900">iEvntür Managed Gigs</p>
              <p className="mt-1">Payouts are processed according to the payout terms displayed on the individual gig.</p>
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
              <p className="font-semibold text-gray-900">Direct Booking &amp; Agency Workspace</p>
              <p className="mt-1">
                Direct Booking and Agency Workspace jobs are paid directly by the booking party and are
                not processed through iEvntür&apos;s payout system unless specifically stated
                otherwise on the booking.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Summary — available/eligible earnings that can be instantly paid out. */}
        {eligible && (eligible.eligible_bookings?.length > 0) && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Available Earnings</p>
                <p className="text-2xl font-bold text-gray-900" data-testid="total-earnings">
                  ${(eligible.total_earnings || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Processing Fee ({payoutInfo?.fee_percent}%)</p>
                <p className="text-2xl font-bold text-red-500" data-testid="total-fees">
                  -${(eligible.total_fees || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-gray-500 mb-1">Net Payout</p>
                <p className="text-2xl font-bold text-cyan-600" data-testid="total-net">
                  ${(eligible.total_net || 0).toFixed(2)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Eligible Bookings — gigs whose disclosed terms make earnings
            available for Instant Pay now. */}
        {(
          <Card className="mb-8 bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-cyan-500" />
                Instant Pay-Eligible Earnings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {(!eligible?.eligible_bookings || eligible.eligible_bookings.length === 0) ? (
                <div className="p-8 text-center text-gray-500">
                  <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p>No gigs currently have Instant Pay-eligible earnings</p>
                  <p className="text-sm mt-1">When you complete an Instant Pay gig with approved hours, your eligible earnings show here. Each gig&apos;s payout terms are shown on the gig itself.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {eligible?.eligible_bookings?.map((booking) => (
                    <div key={booking.booking_id} className="p-4 hover:bg-gray-50 transition-colors" data-testid="eligible-booking">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-gray-900">{booking.venue_name}</h4>
                          <p className="text-sm text-gray-500">{booking.company_name}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span>{booking.shift_date ? formatDateMonthDay(booking.shift_date) : 'N/A'}</span>
                            <span>{booking.hours_worked}hrs @ ${booking.hourly_rate}/hr</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">${booking.gross_earnings.toFixed(2)}</p>
                          <p className="text-xs text-red-500">Fee ({booking.fee_percent}%): -${booking.fee.toFixed(2)}</p>
                          <p className="text-sm font-semibold text-cyan-600">Net: ${booking.net_payout.toFixed(2)}</p>
                          {booking.net_payout >= 20 ? (
                            <Button
                              size="sm"
                              className="mt-2 bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => setSelectedBooking(booking)}
                              disabled={!featureEnabled}
                              title={!featureEnabled ? 'Instant Pay is temporarily disabled' : undefined}
                              data-testid="request-payout-btn"
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              Instant Payout
                            </Button>
                          ) : (
                            <p className="mt-2 text-xs text-gray-400">Below $20 minimum</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Payout History */}
        {history.length > 0 && (
          <Card className="bg-white border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-900">Payout History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-gray-100">
                {history.map((p) => (
                  <div key={p.id} className="p-4 flex justify-between items-center" data-testid="payout-history-item">
                    <div>
                      <h4 className="font-medium text-gray-900">{p.venue_name}</h4>
                      <p className="text-xs text-gray-500">{p.requested_at ? formatDateMonthDay(p.requested_at) : ''}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900">${p.net_amount.toFixed(2)}</span>
                      <Badge className={
                        p.status === 'completed' ? 'bg-green-100 text-green-700' :
                        p.status === 'processing' ? 'bg-cyan-100 text-cyan-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {p.status === 'completed' ? <CheckCircle className="w-3 h-3 mr-1" /> :
                         p.status === 'processing' ? <Clock className="w-3 h-3 mr-1" /> : null}
                        {p.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirm Dialog with Detailed Payout Breakdown */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent>
          {selectedBooking && (
            <>
              <DialogHeader>
                <DialogTitle>Confirm Instant Payout</DialogTitle>
                <DialogDescription>
                  Review the detailed payout breakdown before confirming.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4" data-testid="payout-breakdown">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Gig</span>
                  <span className="font-medium">{selectedBooking.venue_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Hours Worked</span>
                  <span className="font-medium">{selectedBooking.hours_worked} hrs @ ${selectedBooking.hourly_rate}/hr</span>
                </div>
                <div className="border-t pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 font-medium">Gross Payout</span>
                    <span className="font-medium">${selectedBooking.gross_earnings.toFixed(2)}</span>
                  </div>
                </div>
                {selectedBooking.deductions > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Deductions</span>
                    <span className="text-red-500">-${selectedBooking.deductions.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Instant Pay Fee ({payoutInfo?.fee_percent}%)</span>
                  <span className="text-red-500">-${selectedBooking.fee.toFixed(2)}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-bold text-gray-900">Net Payout Received</span>
                  <span className="text-xl font-bold text-cyan-600">${selectedBooking.net_payout.toFixed(2)}</span>
                </div>
              </div>
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-sm">
                  Instant Pay transfers are typically fast, but exact timing depends on your bank and Stripe. This action cannot be undone.
                </AlertDescription>
              </Alert>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedBooking(null)} data-testid="cancel-payout-btn">Cancel</Button>
                <Button
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                  onClick={handleRequestPayout}
                  disabled={requesting}
                  data-testid="confirm-payout-btn"
                >
                  {requesting ? 'Processing...' : 'Confirm Instant Payout'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstantPayout;
